import {
  type Experiment,
  type ExperimentEvaluationOptions,
  useStatsigClient,
} from "@statsig/react-bindings";
import {
  registerStatsigExperiment,
  mixpanelTrackExperiment,
} from "utils/tracking/mixpanel";
import {
  type StatsigExpValues,
  type StatsigExperimentName,
} from "utils/experiments/statsig";

type FullExperimentEvaluationOptions = ExperimentEvaluationOptions & {
  dontTrack?: boolean;
};

type RealizedExperimentData<Name extends StatsigExperimentName> = Pick<
  Experiment,
  "ruleID" | "name"
> & {
  groupName: string;
  value: StatsigExpValues[Name];
};

export function useStatsigExperiment(
  baseOptions?: FullExperimentEvaluationOptions,
) {
  const { client, getExperiment, logEvent } = useStatsigClient();
  const context = client.getContext();

  function mergeOptions(
    overrideOptions?: FullExperimentEvaluationOptions,
  ): FullExperimentEvaluationOptions {
    return { ...baseOptions, ...overrideOptions };
  }

  function getExpData<Name extends StatsigExperimentName>(
    experimentName: Name,
    overrideOptions?: FullExperimentEvaluationOptions,
  ): RealizedExperimentData<Name> {
    const exp = getExperiment(experimentName, mergeOptions(overrideOptions));
    return {
      name: exp.name,
      ruleID: exp.ruleID,
      groupName: exp.groupName || "",
      value: exp.value as StatsigExpValues[Name],
    };
  }

  function isTrackable<Name extends StatsigExperimentName>(
    expData: Pick<Experiment, "ruleID" | "groupName" | "value">,
    options: FullExperimentEvaluationOptions,
  ): expData is RealizedExperimentData<Name> {
    const { value, ruleID, groupName } = expData;
    return Boolean(
      value &&
        typeof value === "object" &&
        Object.keys(value).length &&
        groupName &&
        ruleID &&
        !options?.dontTrack &&
        !["override", "launchedGroup"].some((dontTrackIds) =>
          ruleID.includes(dontTrackIds),
        ),
    );
  }

  function registerExperiment<Name extends StatsigExperimentName>(
    experimentName: Name,
    expData?: RealizedExperimentData<Name>,
    overrideOptions?: FullExperimentEvaluationOptions,
  ) {
    const options = mergeOptions(overrideOptions);
    const realizedExpData = expData ?? getExpData(experimentName, options);
    if (isTrackable<Name>(realizedExpData, options)) {
      registerStatsigExperiment(
        experimentName,
        realizedExpData.groupName,
        context.stableID,
        realizedExpData,
      );
    }
  }

  function trackExperimentView<Name extends StatsigExperimentName>(
    experimentName: Name,
    expData?: RealizedExperimentData<Name>,
    overrideOptions?: FullExperimentEvaluationOptions,
  ) {
    const options = mergeOptions(overrideOptions);
    const realizedExpData = expData ?? getExpData(experimentName, options);

    if (isTrackable<Name>(realizedExpData, options)) {
      registerExperiment(experimentName, realizedExpData, options);
      const eventData = {
        experiment_name: experimentName,
        experiment_variant: realizedExpData.groupName,
        ...(context.stableID && { experiment_stable_id: context.stableID }),
      };
      mixpanelTrackExperiment("Experiment Viewed", eventData, {
        onceWithUniqueKey: experimentName + context.session.data.sessionID,
      });
      logEvent("Experiment Viewed", experimentName, eventData);
    }
  }

  function getExperimentValue<Name extends StatsigExperimentName>(
    experimentName: Name,
    overrideOptions?: FullExperimentEvaluationOptions,
  ): StatsigExpValues[Name] {
    const expData = getExpData(experimentName, overrideOptions);

    trackExperimentView(experimentName, expData, overrideOptions);

    return expData.value;
  }

  return {
    getExperimentValue,
    registerExperiment,
    trackExperimentView,
  };
}
